import type { MouseEventHandler } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Popup } from '@elfsight/widget-modal';
import { getBreakpointsValues } from '@elfsight/widget-ui';
import { openWindowByLink } from '@elfsight/widget-utils';
import type { Rect } from 'react-use-rect';
import { useRect } from 'react-use-rect';
import { MediaVideo } from '../MediaVideo';
import type { PlayerAPI } from '../types';
import { VideoPreview } from '../video-preview/video-preview';

interface VideoComponentProps {
  mode: 'player' | 'popup' | 'newTab' | 'inline';
  videoUrl: string;
  containerWidth?: string;
  popupPortalContainerElement?: Element;
}

const RATIO = 9 / 16;
const DEFAULT_POPUP_WIDTH = 1280;

export const VideoComponent = ({
  mode = 'popup',
  videoUrl,
  popupPortalContainerElement,
  containerWidth = 'content'
}: VideoComponentProps) => {
  const [popupWidth, setPopupWidth] = useState<number>(DEFAULT_POPUP_WIDTH);

  const [rect, setRect] = useState<Rect | null>(null);
  const [rectRef] = useRect(setRect, { resize: true });

  const [popup, setPopup] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);

  const videoControllerRef = useRef<PlayerAPI | null>(null);

  const newTabAction: MouseEventHandler = (event) =>
    openWindowByLink(event, {
      type: 'url',
      value: videoUrl,
      rawValue: videoUrl,
      target: '_blank'
    });
  const action = {
    popup: () => setPopup(true),
    inline: () => setShowPlayer(true),
    newTab: newTabAction,
    player: undefined
  }[mode];

  const handleClick: MouseEventHandler = (event) => {
    event.stopPropagation();
    if (action) {
      action(event);
    }
  };

  const updatePopupWidthBasedWindowHeight = () => {
    const popupHeight = popupWidth * RATIO;
    const windowHeight = window.innerHeight;
    if (popupHeight > windowHeight) {
      setPopupWidth(windowHeight / RATIO);
    } else {
      setPopupWidth(DEFAULT_POPUP_WIDTH);
    }
  };

  useEffect(() => {
    updatePopupWidthBasedWindowHeight();
    window.addEventListener('resize', updatePopupWidthBasedWindowHeight);
    return () =>
      window.removeEventListener('resize', updatePopupWidthBasedWindowHeight);
  }, []);

  const borderAndIconSize = getBreakpointsValues<any>(Number(rect?.width), {
    640: {
      border: 16,
      playIconSize: 64
    },
    240: {
      border: 12,
      playIconSize: 48
    },
    120: {
      border: 8,
      playIconSize: 24
    },
    1: {
      border: 4,
      playIconSize: 18
    }
  });

  if (mode === 'player' || (mode === 'inline' && showPlayer)) {
    return (
      <Container
        _width={containerWidth}
        ref={rectRef}
        _borderRadius={borderAndIconSize.border}
      >
        <MediaVideo
          ref={videoControllerRef}
          playerRatio={RATIO}
          src={videoUrl}
          autoplay={mode === 'inline' && showPlayer}
        />
      </Container>
    );
  }
  return (
    <>
      <Popup
        portalContainerElement={popupPortalContainerElement}
        width={popupWidth}
        show={popup}
        onCloseRequest={() => setPopup(false)}
        closeControlPosition="outside"
        backdropColor="rgba(0, 0, 0, 0.7)"
        withCloseControl
        withBackdrop
        closeOnOutsideClick
        closeOnEscape
        fixed
      >
        <MediaVideoContainer>
          <MediaVideo
            ratio={RATIO}
            playerRatio={RATIO}
            src={videoUrl}
            autoplay
          />
        </MediaVideoContainer>
      </Popup>
      <Container
        ref={rectRef}
        _width={containerWidth}
        _borderRadius={borderAndIconSize.border}
      >
        <VideoPreview
          iconSize={borderAndIconSize.playIconSize}
          onClick={handleClick}
          videoUrl={videoUrl}
          containerWidth={containerWidth}
        />
      </Container>
    </>
  );
};

const Container = styled.div<{ _borderRadius?: number; _width: string }>`
  border-radius: ${({ _borderRadius = 0 }) => _borderRadius}px;
  width: ${({ _width }) => _width};
  overflow: hidden;
`;

const MediaVideoContainer = styled.div`
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
`;
